import {
  AiutoAppShell,
  ProductDataProvider,
  type ProductUser,
} from "@aiuto/components";
import {
  addSettingsUserApi,
  addSingleToCollectionApi,
  archiveLibraryImageApi,
  archivePersonalCollectionApi,
  archiveSettingsUserApi,
  changeUserRoleApi,
  confirmAddToCollectionApi,
  createPersonalCollectionApi,
  getMiroSyncStatusApi,
  reactivateSettingsUserApi,
  removeSingleFromCollectionApi,
  renameCanonicalCollectionApi,
  renamePersonalCollectionApi,
  reorderPersonalCollectionsApi,
  restoreArchivedCollectionApi,
  saveAssetMetaApi,
  setMetadataPromptApi,
  setTagSuggestionPromptApi,
  signOutApi,
  startMiroSyncBoardsApi,
  stopMiroSyncBoardsApi,
  submitTaggingViaApi,
} from "@aiuto/components";
import { DEFAULT_ASSET_BLOB_PRESIGN_TTL_SECONDS } from "@aiuto/domain";
import { useCallback, useMemo, type ReactNode } from "react";

export type ProductShellProps = {
  currentUser: ProductUser;
  children: ReactNode;
};

export const ProductShell = ({ currentUser, children }: ProductShellProps) => {
  const actions = useMemo(
    () => ({
      onAddToCollConfirm: confirmAddToCollectionApi,
      onAddSingleToCollection: addSingleToCollectionApi,
      onRemoveSingleFromCollection: removeSingleFromCollectionApi,
      onArchive: archiveLibraryImageApi,
      onSaveMeta: saveAssetMetaApi,
      onRenameCanonical: renameCanonicalCollectionApi,
      onRenamePersonal: renamePersonalCollectionApi,
      onRestoreCollection: restoreArchivedCollectionApi,
      onArchivePersonalCollection: archivePersonalCollectionApi,
      onAddUser: addSettingsUserApi,
      onChangeRole: changeUserRoleApi,
      onArchiveUser: archiveSettingsUserApi,
      onReactivateUser: reactivateSettingsUserApi,
      onSetMetadataPrompt: setMetadataPromptApi,
      onSetTagPrompt: setTagSuggestionPromptApi,
    }),
    [],
  );

  const miroSync = useMemo(
    () => ({
      onStartMiroSync: startMiroSyncBoardsApi,
      onStopMiroSync: stopMiroSyncBoardsApi,
      onGetMiroSyncStatus: getMiroSyncStatusApi,
    }),
    [],
  );

  const onSubmitTagging = useCallback(
    (payload: Parameters<typeof submitTaggingViaApi>[0]) => submitTaggingViaApi(payload),
    [],
  );

  return (
    <ProductDataProvider
      currentUser={currentUser}
      persistPersonalCollectionOrder={reorderPersonalCollectionsApi}
    >
      <AiutoAppShell
        actions={actions}
        presignTtlSeconds={DEFAULT_ASSET_BLOB_PRESIGN_TTL_SECONDS}
        onCreatePersonal={createPersonalCollectionApi}
        onSignOut={signOutApi}
        onSubmitTagging={onSubmitTagging}
        miroSync={miroSync}
      >
        {children}
      </AiutoAppShell>
    </ProductDataProvider>
  );
};
